import React, { useState, useEffect } from 'react';
import { CreditCard, Upload, CheckCircle, XCircle, AlertCircle, Building2, Edit3, Eye, Save, X } from 'lucide-react';
import './BankDetails.css';
import { API_BASE_URL } from '../../api';

const BankDetails = () => {
  const [bankDetails, setBankDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    account_holder_name: '',
    account_number: '',
    account_number_confirm: '',
    ifsc_code: '',
    upi_id: '',
    cancelled_cheque: null
  });
  const [originalFormData, setOriginalFormData] = useState(null);
  const [errors, setErrors] = useState({});
  const [bankInfo, setBankInfo] = useState(null);
  const [validatingIFSC, setValidatingIFSC] = useState(false);

  useEffect(() => {
    fetchBankDetails();
  }, []);

  const fetchBankDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/vendor/bank-details`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBankDetails(data.bankDetails);
        if (data.bankDetails) {
          const newFormData = {
            account_holder_name: data.bankDetails.account_holder_name || '',
            account_number: '', // Don't show account number for security
            account_number_confirm: '',
            ifsc_code: data.bankDetails.ifsc_code || '',
            upi_id: data.bankDetails.upi_id || '',
            cancelled_cheque: null
          };
          setFormData(newFormData);
          setOriginalFormData(newFormData);
          setIsEditMode(false);
        }
      }
    } catch (error) {
      console.error('Error fetching bank details:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateIFSC = async (ifsc) => {
    if (!ifsc || ifsc.length < 11) return;
    
    setValidatingIFSC(true);
    try {
      // Simulate IFSC validation - in real implementation, this would call the backend
      const response = await fetch(`https://ifsc.razorpay.com/${ifsc.toUpperCase()}`);
      if (response.ok) {
        const bankData = await response.json();
        setBankInfo({
          bankName: bankData.BANK,
          branch: bankData.BRANCH,
          address: bankData.ADDRESS,
          city: bankData.CITY,
          state: bankData.STATE
        });
        setErrors(prev => ({ ...prev, ifsc_code: null }));
      } else {
        setBankInfo(null);
        setErrors(prev => ({ ...prev, ifsc_code: 'Invalid IFSC code' }));
      }
    } catch (error) {
      setBankInfo(null);
      setErrors(prev => ({ ...prev, ifsc_code: 'Error validating IFSC code' }));
    } finally {
      setValidatingIFSC(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }

    // Validate IFSC when it's complete
    if (name === 'ifsc_code' && value.length === 11) {
      validateIFSC(value);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, cancelled_cheque: 'Only JPG, PNG, and PDF files are allowed' }));
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, cancelled_cheque: 'File size must be less than 5MB' }));
        return;
      }
      
      setFormData(prev => ({ ...prev, cancelled_cheque: file }));
      setErrors(prev => ({ ...prev, cancelled_cheque: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.account_holder_name.trim()) {
      newErrors.account_holder_name = 'Account holder name is required';
    } else if (formData.account_holder_name.trim().length < 2) {
      newErrors.account_holder_name = 'Account holder name must be at least 2 characters';
    }

    if (!formData.account_number.trim()) {
      newErrors.account_number = 'Account number is required';
    } else if (!/^[0-9]{9,18}$/.test(formData.account_number)) {
      newErrors.account_number = 'Account number must be 9-18 digits';
    }

    if (!formData.account_number_confirm.trim()) {
      newErrors.account_number_confirm = 'Please confirm account number';
    } else if (formData.account_number !== formData.account_number_confirm) {
      newErrors.account_number_confirm = 'Account numbers do not match';
    }

    if (!formData.ifsc_code.trim()) {
      newErrors.ifsc_code = 'IFSC code is required';
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc_code.toUpperCase())) {
      newErrors.ifsc_code = 'Invalid IFSC code format';
    } else if (!bankInfo) {
      newErrors.ifsc_code = 'Please wait for IFSC validation';
    }

    if (formData.upi_id && !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+$/.test(formData.upi_id)) {
      newErrors.upi_id = 'Invalid UPI ID format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEdit = () => {
    setIsEditMode(true);
    setErrors({});
  };

  const handleCancel = () => {
    if (originalFormData) {
      setFormData(originalFormData);
    }
    setIsEditMode(false);
    setErrors({});
    setBankInfo(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      
      formDataToSend.append('account_holder_name', formData.account_holder_name);
      formDataToSend.append('account_number', formData.account_number);
      formDataToSend.append('account_number_confirm', formData.account_number_confirm);
      formDataToSend.append('ifsc_code', formData.ifsc_code.toUpperCase());
      formDataToSend.append('upi_id', formData.upi_id || '');
      
      if (formData.cancelled_cheque) {
        formDataToSend.append('cancelled_cheque', formData.cancelled_cheque);
      }

      const response = await fetch(`${API_BASE_URL}/api/vendor/bank-details`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataToSend
      });

      const data = await response.json();
      
      if (response.ok) {
        alert('Bank details saved successfully!');
        await fetchBankDetails();
        setFormData(prev => ({
          ...prev,
          account_number: '',
          account_number_confirm: '',
          cancelled_cheque: null
        }));
        setIsEditMode(false);
      } else {
        alert(data.error || 'Failed to save bank details');
      }
    } catch (error) {
      console.error('Error saving bank details:', error);
      alert('Error saving bank details');
    } finally {
      setSaving(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'VERIFIED':
        return <CheckCircle size={16} className="status-verified" />;
      case 'REJECTED':
        return <XCircle size={16} className="status-rejected" />;
      case 'PENDING':
        return <AlertCircle size={16} className="status-pending" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'VERIFIED':
        return 'status-verified';
      case 'REJECTED':
        return 'status-rejected';
      case 'PENDING':
        return 'status-pending';
      default:
        return '';
    }
  };

  if (loading) {
    return <div className="loading">Loading bank details...</div>;
  }

  return (
    <div className="bank-details">
      <div className="bank-details-header">
        <div className="bank-details-title">
          <CreditCard size={20} />
          Bank Details
        </div>
        <div className="bank-details-subtitle">
          Manage your bank account information for payouts
        </div>
      </div>

      {bankDetails && (
        <div className="bank-details-status">
          <div className="status-card">
            <div className="status-header">
              <span className="status-label">Verification Status</span>
              <span className={`status-badge ${getStatusColor(bankDetails.verification_status)}`}>
                {bankDetails.verification_status}
                {getStatusIcon(bankDetails.verification_status)}
              </span>
            </div>
            {bankDetails.verification_notes && (
              <div className="status-notes">
                <strong>Notes:</strong> {bankDetails.verification_notes}
              </div>
            )}
            {bankDetails.verified_at && (
              <div className="status-date">
                Verified on: {new Date(bankDetails.verified_at).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bank-details-form-container">
        {bankDetails && !isEditMode ? (
          // VIEW MODE
          <div className="bank-details-view">
            <div className="view-header">
              <h3>Bank Account Information</h3>
              <button 
                type="button" 
                onClick={handleEdit}
                className="btn-edit"
              >
                <Edit3 size={16} />
                Edit Details
              </button>
            </div>
            
            <div className="view-content">
              <div className="view-row">
                <label>Account Holder Name:</label>
                <span>{bankDetails.account_holder_name}</span>
              </div>
              
              <div className="view-row">
                <label>Bank Name:</label>
                <span>{bankDetails.bank_name}</span>
              </div>
              
              <div className="view-row">
                <label>IFSC Code:</label>
                <span>{bankDetails.ifsc_code}</span>
              </div>
              
              {bankDetails.upi_id && (
                <div className="view-row">
                  <label>UPI ID:</label>
                  <span>{bankDetails.upi_id}</span>
                </div>
              )}
              
              <div className="view-row">
                <label>Account Number:</label>
                <span className="account-number-masked">
                  ••••••••••{bankDetails.account_number_encrypted ? ' (Encrypted)' : ''}
                </span>
              </div>
            </div>
          </div>
        ) : (
          // EDIT MODE
          <form onSubmit={handleSubmit} className="bank-details-form">
            <div className="form-section">
              <div className="form-header">
                <h3>Account Information</h3>
                {bankDetails && (
                  <button 
                    type="button" 
                    onClick={handleCancel}
                    className="btn-cancel"
                  >
                    <X size={16} />
                    Cancel
                  </button>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="account_holder_name">Account Holder Name *</label>
                <input
                  type="text"
                  id="account_holder_name"
                  name="account_holder_name"
                  value={formData.account_holder_name}
                  onChange={handleInputChange}
                  className={errors.account_holder_name ? 'error' : ''}
                  placeholder="Enter account holder name"
                />
                {errors.account_holder_name && (
                  <span className="error-message">{errors.account_holder_name}</span>
                )}
              </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="account_number">Account Number *</label>
                <input
                  type="text"
                  id="account_number"
                  name="account_number"
                  value={formData.account_number}
                  onChange={handleInputChange}
                  className={errors.account_number ? 'error' : ''}
                  placeholder="Enter account number"
                  maxLength="18"
                />
                {errors.account_number && (
                  <span className="error-message">{errors.account_number}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="account_number_confirm">Confirm Account Number *</label>
                <input
                  type="text"
                  id="account_number_confirm"
                  name="account_number_confirm"
                  value={formData.account_number_confirm}
                  onChange={handleInputChange}
                  className={errors.account_number_confirm ? 'error' : ''}
                  placeholder="Re-enter account number"
                  maxLength="18"
                />
                {errors.account_number_confirm && (
                  <span className="error-message">{errors.account_number_confirm}</span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="ifsc_code">IFSC Code *</label>
              <div className="ifsc-input-container">
                <input
                  type="text"
                  id="ifsc_code"
                  name="ifsc_code"
                  value={formData.ifsc_code}
                  onChange={handleInputChange}
                  className={errors.ifsc_code ? 'error' : ''}
                  placeholder="Enter IFSC code (e.g., SBIN0001234)"
                  maxLength="11"
                  style={{ textTransform: 'uppercase' }}
                />
                {validatingIFSC && (
                  <div className="validating-indicator">Validating...</div>
                )}
              </div>
              {errors.ifsc_code && (
                <span className="error-message">{errors.ifsc_code}</span>
              )}
              {bankInfo && (
                <div className="bank-info">
                  <Building2 size={16} />
                  <div>
                    <strong>{bankInfo.bankName}</strong>
                    <div>{bankInfo.branch}, {bankInfo.city}, {bankInfo.state}</div>
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="upi_id">UPI ID (Optional)</label>
              <input
                type="text"
                id="upi_id"
                name="upi_id"
                value={formData.upi_id}
                onChange={handleInputChange}
                className={errors.upi_id ? 'error' : ''}
                placeholder="Enter UPI ID (e.g., username@bankname)"
              />
              {errors.upi_id && (
                <span className="error-message">{errors.upi_id}</span>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3>Document Upload</h3>
            
            <div className="form-group">
              <label htmlFor="cancelled_cheque">Cancelled Cheque (Optional)</label>
              <div className="file-upload-container">
                <input
                  type="file"
                  id="cancelled_cheque"
                  name="cancelled_cheque"
                  onChange={handleFileChange}
                  accept=".jpg,.jpeg,.png,.pdf"
                  className="file-input"
                />
                <label htmlFor="cancelled_cheque" className="file-upload-label">
                  <Upload size={16} />
                  {formData.cancelled_cheque ? formData.cancelled_cheque.name : 'Choose file'}
                </label>
              </div>
              <div className="file-upload-help">
                Upload a clear image or PDF of your cancelled cheque (Max 5MB)
              </div>
              {errors.cancelled_cheque && (
                <span className="error-message">{errors.cancelled_cheque}</span>
              )}
            </div>
          </div>

            <div className="form-actions">
              <button
                type="submit"
                disabled={saving || validatingIFSC}
                className="btn-primary"
              >
                {saving ? 'Saving...' : (bankDetails ? 'Update Bank Details' : 'Save Bank Details')}
              </button>
            </div>
          </form>
        )}
      </div>

      {bankDetails && bankDetails.verification_status === 'PENDING' && (
        <div className="verification-notice">
          <AlertCircle size={16} />
          <div>
            <strong>Verification Pending</strong>
            <p>Your bank details are under review. You will be notified once verification is complete.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankDetails;
